import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Phone, 
  Mail, 
  Edit3, 
  Save, 
  X, 
  Camera,
  Sparkles,
  ArrowLeft
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import VIPSectionCard from '../components/VIPSectionCard';

interface ProfileFormData {
  mobile: string;
  email: string;
  gender: 'male' | 'female' | 'unisex';
  interests: string[];
  showAdultProducts: boolean;
}

const AVAILABLE_INTERESTS = [
  'Sports', 'Fitness', 'Fashion', 'Technology', 'Gaming', 
  'Travel', 'Food', 'Music', 'Books', 'Art', 
  'Photography', 'Movies', 'Beauty', 'Health'
];

export default function Profile() {
  const { user, updateUser, refreshUserProfile, token } = useAuth();
  const [formData, setFormData] = useState<ProfileFormData>({
    mobile: '',
    email: '',
    gender: 'unisex',
    interests: [],
    showAdultProducts: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<ProfileFormData>({
    mobile: '',
    email: '',
    gender: 'unisex',
    interests: [],
    showAdultProducts: false,
  });

  // Initialize form data from user profile
  useEffect(() => {
    if (user) {
      const initialData: ProfileFormData = {
        mobile: user.profile?.phone_number || '',
        email: user.email || '',
        gender: user.preferences?.gender || 'unisex',
        interests: user.preferences?.interests || [],
        showAdultProducts: user.preferences?.show_adult_products || false,
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
    }
  }, [user]);

  // Track form changes
  useEffect(() => {
    const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    setHasChanges(hasFormChanges);
  }, [formData, originalFormData]);

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const updatePayload = {
        phone_number: formData.mobile,
        email: formData.email,
        preferences: {
          gender: formData.gender,
          interests: formData.interests,
          show_adult_products: formData.showAdultProducts,
          filled: true,
        }
      };

      const response = await fetch('/api/user/update-profile/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });

      if (response.ok) {
        const updatedData = await response.json();
        
        // Update local auth context
        await refreshUserProfile();
        
        setOriginalFormData(formData);
        setHasChanges(false);
        toast.success('Profile updated successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(originalFormData);
    setHasChanges(false);
  };

  const handleMobileEdit = () => {
    // For now, just allow editing. You can add OTP verification later
    setIsEditing(true);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            backdropFilter: 'blur(8px)',
          },
          success: {
            iconTheme: {
              primary: '#00d4ff',
              secondary: '#ffffff',
            },
          },
        }}
      />
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => window.history.back()}
              className="hover:bg-gray-800/50"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gradient">My Profile</h1>
              <p className="text-gray-400 mt-1">Manage your account and preferences</p>
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="space-y-6">
          <VIPSectionCard
            token={token}
            isVip={Boolean(user?.profile?.is_vip)}
            vipExpiry={user?.profile?.vip_expiry}
          />

          {/* Account Information Section */}
          <div className="cosmic-card p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-cosmic flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Account Information</h2>
            </div>

            {/* Profile Picture (Optional) */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-cosmic flex items-center justify-center relative overflow-hidden">
                {user?.username ? (
                  <span className="text-2xl font-bold text-white">
                    {user.username.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <User size={32} className="text-white" />
                )}
              </div>
              <div>
                <Button variant="secondary" size="sm" className="gap-2">
                  <Camera size={16} />
                  Change Photo
                </Button>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
              </div>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={user?.username || ''}
                disabled
                className="bg-gray-800/30 text-gray-400"
              />
            </div>

            {/* Mobile Number */}
            <div className="space-y-2">
              <Label htmlFor="mobile" className="flex items-center gap-2">
                <Phone size={16} />
                Mobile Number
              </Label>
              <div className="flex gap-2">
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="+91 XXXXX XXXXX"
                  className="flex-1"
                  readOnly={!isEditing}
                />
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={handleMobileEdit}
                  className="gap-2 whitespace-nowrap"
                >
                  <Edit3 size={16} />
                  {isEditing ? 'Verify' : 'Edit'}
                </Button>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your.email@example.com"
              />
            </div>
          </div>

          {/* Preferences Section */}
          <div className="cosmic-card p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cosmic-purple to-mystery-glow flex items-center justify-center">
                <Sparkles size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Preferences</h2>
            </div>

            {/* Gender Selection */}
            <div className="space-y-3">
              <Label>Gender</Label>
              <RadioGroup 
                value={formData.gender} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value as 'male' | 'female' | 'unisex' }))}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="male" />
                  <Label htmlFor="male" className="cursor-pointer">Male</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="female" />
                  <Label htmlFor="female" className="cursor-pointer">Female</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="unisex" id="unisex" />
                  <Label htmlFor="unisex" className="cursor-pointer">Unisex</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Interests */}
            <div className="space-y-3">
              <Label>Interests</Label>
              <p className="text-sm text-gray-400">Select topics that interest you</p>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_INTERESTS.map((interest) => {
                  const isSelected = formData.interests.includes(interest);
                  return (
                    <Badge
                      key={interest}
                      variant={isSelected ? "default" : "outline"}
                      className={`cursor-pointer transition-all hover:scale-105 ${
                        isSelected 
                          ? 'bg-gradient-to-r from-electric-cyan/30 to-cosmic-purple/30 border-electric-cyan/50 text-electric-cyan' 
                          : 'hover:border-electric-cyan/30 hover:text-electric-cyan'
                      }`}
                      onClick={() => handleInterestToggle(interest)}
                    >
                      {interest}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Adult Products Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-gray-700 bg-gray-800/20">
              <div className="space-y-1">
                <Label htmlFor="adult-products" className="text-base">Adult Products</Label>
                <p className="text-sm text-gray-400">Show adult and 18+ products in recommendations</p>
              </div>
              <Switch
                id="adult-products"
                checked={formData.showAdultProducts}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, showAdultProducts: checked }))}
              />
            </div>
          </div>

          {/* Action Buttons */}
          {hasChanges && (
            <div className="flex gap-3 justify-end cosmic-card p-4">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                className="gap-2"
              >
                <X size={16} />
                Cancel
              </Button>
              <Button 
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="gap-2"
              >
                <Save size={16} />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}